import React from "react";
import { Helmet } from "react-helmet-async";
import "./FieldTesterPro.css";

const steps = [
  {
    number: "01",
    title: "Use ASDRY",
    copy: "Create a report for an activity you were already planning.",
  },
  {
    number: "02",
    title: "Do your activity",
    copy: "Use ASDRY's report as part of your normal preparation.",
  },
  {
    number: "03",
    title: "Tell us what happened",
    copy: "After eligible Field Tester reports, ASDRY asks a short set of questions about the conditions you actually experienced.",
  },
  {
    number: "04",
    title: "Help ASDRY improve",
    copy: "Your observations help show where ASDRY matched real conditions and where its interpretation can improve.",
  },
];

const feedbackQuestions = [
  "Did the overall conditions match?",
  "Was the surface wetter or drier than expected?",
  "Was the wind stronger or lighter than expected?",
  "Did it feel warmer or cooler than expected?",
  "Did the conditions affect the activity more or less than expected?",
  "Was the preparation advice useful?",
  "What, if anything, did ASDRY miss?",
];

const benefits = [
  "Access to Field Tester Pro while actively participating",
  "Deeper planning and adaptation guidance than the standard Free experience",
  "The opportunity to help shape how ASDRY develops",
  "Participation in a real-world testing programme",
];

const activities = [
  "Walking",
  "Running",
  "Cycling",
  "Hiking",
  "Dog walking",
  "Commuting",
  "Gardening",
  "Parkrun",
  "School run",
  "Everyday outdoor plans",
];

function FieldTesterPro() {
  return (
    <div className="field-tester-page">
      <Helmet>
        <title>Field Tester Pro | ASDRY</title>
        <meta
          name="description"
          content="Help test ASDRY in real conditions. Try ASDRY Free first, then apply for Field Tester Pro from your account and help improve real-world environmental guidance."
        />
      </Helmet>

      <section className="field-tester-hero" aria-labelledby="field-tester-title">
        <div className="field-tester-hero-copy">
          <p className="field-tester-eyebrow">FIELD TESTER PRO</p>
          <h1 className="field-tester-title" id="field-tester-title">
            Help us test ASDRY in real conditions.
          </h1>
          <p className="field-tester-lead">
            Use ASDRY for activities you&apos;re already planning, compare its report
            with what you actually experience, and help us improve how ASDRY
            interprets real-world conditions.
          </p>
          <div className="field-tester-actions">
            <a className="field-tester-button field-tester-button-primary" href="https://asdry.app/">
              Try ASDRY Free →
            </a>
            <a className="field-tester-text-link" href="#how-field-testing-works">
              Already use ASDRY? See how Field Tester Pro works ↓
            </a>
          </div>
        </div>
      </section>

      <section className="field-tester-section field-tester-introduction" aria-labelledby="field-tester-intro-title">
        <div className="field-tester-section-heading">
          <p className="field-tester-eyebrow">WHAT IT IS</p>
          <h2 id="field-tester-intro-title">Real activities. Real conditions. Useful feedback.</h2>
        </div>
        <div className="field-tester-intro-grid">
          <p>
            Field Tester Pro is ASDRY&apos;s real-world testing programme. Forecast
            and environmental data tell ASDRY what conditions are expected.
          </p>
          <p>
            Field observations help test whether ASDRY interpreted those conditions
            usefully for the activity. That practical comparison helps ASDRY improve.
          </p>
        </div>
      </section>

      <section
        className="field-tester-section field-tester-process"
        id="how-field-testing-works"
        aria-labelledby="field-tester-process-title"
      >
        <div className="field-tester-section-heading">
          <p className="field-tester-eyebrow">THE PROCESS</p>
          <h2 id="field-tester-process-title">How Field Testing works</h2>
        </div>
        <ol className="field-tester-step-grid">
          {steps.map((step) => (
            <li className="field-tester-step" key={step.number}>
              <span className="field-tester-step-number" aria-hidden="true">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="field-tester-section field-tester-feedback" aria-labelledby="field-tester-feedback-title">
        <div className="field-tester-section-heading">
          <p className="field-tester-eyebrow">STRUCTURED OBSERVATIONS</p>
          <h2 id="field-tester-feedback-title">What you&apos;ll be asked about</h2>
          <p>
            Feedback is short and structured. Written detail is optional where the
            questions do not need it.
          </p>
        </div>
        <ul className="field-tester-question-list">
          {feedbackQuestions.map((question) => (
            <li key={question}><span aria-hidden="true">✓</span>{question}</li>
          ))}
        </ul>
        <div className="field-tester-note-card">
          <strong>Plans can change.</strong>
          <p>
            If an activity was cancelled or did not take place, record that instead
            of providing feedback that does not reflect a real experience.
          </p>
        </div>
      </section>

      <section className="field-tester-section field-tester-benefits" aria-labelledby="field-tester-benefits-title">
        <div className="field-tester-section-heading">
          <p className="field-tester-eyebrow">ACTIVE PARTICIPATION</p>
          <h2 id="field-tester-benefits-title">What you get as a Field Tester</h2>
          <p>
            Field Tester Pro gives approved participants access to ASDRY&apos;s deeper
            Pro report experience while they actively contribute observations.
          </p>
        </div>
        <ul className="field-tester-benefit-list">
          {benefits.map((benefit) => (
            <li key={benefit}><span aria-hidden="true">✓</span>{benefit}</li>
          ))}
        </ul>

        <article className="field-tester-participation-card">
          <h3>Field Tester Pro is available while you participate.</h3>
          <p>
            Field Tester Pro is provided in return for contributing real-world
            feedback. If participation stops for a period, Field Tester Pro access
            may pause, while your normal ASDRY Free account remains available.
          </p>
        </article>
      </section>

      <section className="field-tester-section field-tester-suitability" aria-labelledby="field-tester-suitability-title">
        <div className="field-tester-section-heading">
          <p className="field-tester-eyebrow">WHO IT IS FOR</p>
          <h2 id="field-tester-suitability-title">You don&apos;t need to be an expert.</h2>
          <p>
            We&apos;re looking for people who already spend time outdoors and are
            willing to compare ASDRY with what they actually experience. You do not
            need specialist weather knowledge or an elite training schedule.
          </p>
        </div>
        <ul className="field-tester-activity-list" aria-label="Example Field Tester activities">
          {activities.map((activity) => <li key={activity}>{activity}</li>)}
        </ul>
      </section>

      <section className="field-tester-section field-tester-early-testing" aria-labelledby="field-tester-early-testing-title">
        <p className="field-tester-eyebrow">EARLY FIELD TESTING</p>
        <h2 id="field-tester-early-testing-title">Help shape Field Tester Pro from the beginning.</h2>
        <p>
          Field Tester Pro is being introduced gradually while we build and validate
          ASDRY&apos;s real-world feedback system.
        </p>
      </section>

      <section className="field-tester-section field-tester-free-first" aria-labelledby="field-tester-free-first-title">
        <div className="field-tester-free-first-main">
          <p className="field-tester-eyebrow">FREE FIRST</p>
          <h2 id="field-tester-free-first-title">Try ASDRY before you apply.</h2>
          <p>
            We think the best Field Testers are people who have actually used ASDRY.
            Start with Free, create reports for your real plans, and decide whether
            ASDRY is useful to you.
          </p>
          <p>
            If you&apos;d like to contribute more, you can apply for Field Tester Pro
            from your ASDRY Account.
          </p>
          <a className="field-tester-button field-tester-button-primary" href="https://asdry.app/">
            Try ASDRY Free →
          </a>
        </div>

        <aside className="field-tester-existing-user">
          <h3>Already using ASDRY?</h3>
          <p>Field Tester applications are available from your ASDRY Account.</p>
          <a className="field-tester-button field-tester-button-secondary" href="https://asdry.app/">
            Open ASDRY →
          </a>
        </aside>
      </section>
    </div>
  );
}

export default FieldTesterPro;
